// ═══════════════════════════════════════════════════════════════════════
// WelcomeScreen.tsx — P2.4 first-arrival landing for new Agentic Operating System users
// ═══════════════════════════════════════════════════════════════════════
// Shown ONCE per Agentic Operating System, when ?welcome=true is in the URL after a magic-link
// arrival (i.e. the user just completed the claim flow).
//
// W1 — rebuilt as a desktop-first INSTALL MENU. Install is the whole point
// of this screen: a user may want the Agentic Operating System on several devices, and desktop is
// strongly recommended. The screen lists all three destinations
// (Desktop/Laptop hero, then iPhone + Android) and each does the right thing
// based on whether the native installPrompt exists:
//   - Desktop hero: installPrompt → native install; else address-bar
//     instruction (Safari/Firefox desktop can't be installed programmatically).
//   - iPhone: always manual (Share → Add to Home Screen) — no native prompt.
//   - Android: installPrompt → native install; else Chrome-menu instruction.
// The native installPrompt does not reveal the device, only that we're on
// Chromium desktop/Android — so it wires to BOTH the Desktop hero and Android.
//
// There is NO browse/skip CTA as the primary action — install is the action.
// A single quiet "Continue to your Agentic Operating System →" link sits at the bottom so users are
// never stranded: onInstall does NOT dismiss this screen, so without it a
// desktop user (post-install) or an iPhone user (manual install) would be
// trapped in this tab.
//
// Wired by App.tsx — when status === "ok_authed" AND welcome flag is true.
// ═══════════════════════════════════════════════════════════════════════

import { Sparkles, Monitor, Smartphone, Share } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface WelcomeScreenProps {
  businessName: string;
  installPrompt: BeforeInstallPromptEvent | null;
  onShowOS: () => void;
  onInstall: () => void;
  // True when the Agentic Operating System is already installed on this device (running standalone,
  // iOS standalone, or a prior install latched in localStorage). When set, we
  // show a "Welcome back" variant instead of pushing the full install hero.
  alreadyInstalled?: boolean;
}

export function WelcomeScreen({
  businessName,
  installPrompt,
  onShowOS,
  onInstall,
  alreadyInstalled = false,
}: WelcomeScreenProps) {
  // ─── WELCOME BACK — already installed on this device ───
  // High-value path: ?welcome=true AND the Agentic Operating System is already installed (a returning
  // installed user who re-hit the post-claim link). Promote "Continue to your Agentic Operating System"
  // to the primary action; demote install to a subordinate "another device" block.
  if (alreadyInstalled) {
    return (
      <div className="min-h-screen w-full bg-[#0A0A0A] flex flex-col items-center justify-center px-6 py-12">
        <div className="max-w-lg w-full text-center">
          {/* Brand mark */}
          <div className="flex items-center justify-center gap-2 mb-10">
            <div className="w-10 h-10 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-emerald-400" strokeWidth={2} />
            </div>
            <span className="font-display text-lg font-semibold tracking-tight text-[#F5F1EA]">
              {businessName}
            </span>
          </div>

          <h1
            className="text-4xl md:text-5xl font-semibold tracking-tight text-[#F5F1EA] mb-4 leading-[1.15]"
            style={{ fontFamily: 'Fraunces, Georgia, serif' }}
          >
            Welcome back to <span className="text-emerald-400">{businessName}</span>.
          </h1>
          <p className="text-base md:text-lg text-[#F5F1EA] leading-relaxed mb-10">
            You're still signed in here. Just tap to open it.
          </p>

          {/* PRIMARY: continue to the Agentic Operating System */}
          <button
            onClick={onShowOS}
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0A0A] font-semibold text-base px-6 py-3.5 rounded-xl transition-colors shadow-sm mb-10"
          >
            Open {businessName} →
          </button>

          {/* SUBORDINATE: install on another device (still reachable, not the hero) */}
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500 text-left mb-3">
            Keep it on another device
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Desktop */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-left">
              <div className="flex items-center gap-2 mb-2">
                <Monitor className="w-4 h-4 text-slate-300 flex-shrink-0" strokeWidth={2} />
                <span className="text-sm font-semibold text-[#F5F1EA]">Desktop</span>
              </div>
              {installPrompt ? (
                <button
                  onClick={onInstall}
                  className="inline-flex items-center gap-1.5 bg-transparent border border-slate-700 hover:border-slate-600 hover:bg-slate-800 text-[#F5F1EA] font-medium text-xs px-3.5 py-2 rounded-lg transition-colors"
                >
                  Install
                </button>
              ) : (
                <p className="text-xs text-slate-400 leading-relaxed">
                  Open this page in <span className="text-slate-200 font-medium">Chrome or Edge</span>, then choose <span className="text-slate-200 font-medium">Add to Home Screen</span> or <span className="text-slate-200 font-medium">Install App</span> from the browser menu.
                </p>
              )}
            </div>
            {/* iPhone */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-left">
              <div className="flex items-center gap-2 mb-2">
                <Share className="w-4 h-4 text-slate-300 flex-shrink-0" strokeWidth={2} />
                <span className="text-sm font-semibold text-[#F5F1EA]">iPhone</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Tap <span className="text-slate-200 font-medium">Share</span>, then <span className="text-slate-200 font-medium">Add to Home Screen</span>.
              </p>
            </div>
            {/* Android */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-left">
              <div className="flex items-center gap-2 mb-2">
                <Smartphone className="w-4 h-4 text-slate-300 flex-shrink-0" strokeWidth={2} />
                <span className="text-sm font-semibold text-[#F5F1EA]">Android</span>
              </div>
              {installPrompt ? (
                <button
                  onClick={onInstall}
                  className="inline-flex items-center gap-1.5 bg-transparent border border-slate-700 hover:border-slate-600 hover:bg-slate-800 text-[#F5F1EA] font-medium text-xs px-3.5 py-2 rounded-lg transition-colors"
                >
                  Install
                </button>
              ) : (
                <p className="text-xs text-slate-400 leading-relaxed">
                  Open in <span className="text-slate-200 font-medium">Chrome</span>, then choose <span className="text-slate-200 font-medium">Add to Home Screen</span> or <span className="text-slate-200 font-medium">Install App</span> from the browser menu.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#0A0A0A] flex flex-col items-center justify-center px-6 py-12">
      <div className="max-w-lg w-full text-center">

        {/* Brand mark */}
        <div className="flex items-center justify-center gap-2 mb-10">
          <div className="w-10 h-10 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-emerald-400" strokeWidth={2} />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight text-[#F5F1EA]">
            {businessName}
          </span>
        </div>

        {/* Headline */}
        <h1
          className="text-4xl md:text-5xl font-semibold tracking-tight text-[#F5F1EA] mb-4 leading-[1.15]"
          style={{ fontFamily: 'Fraunces, Georgia, serif' }}
        >
          Welcome to <span className="text-emerald-400">{businessName}</span>.
        </h1>

        {/* Subhead — desktop-first advisory, deliberately prominent */}
        <p className="text-base md:text-lg text-[#F5F1EA] leading-relaxed mb-10">
          Add it once on the devices you use most. After that, just tap the icon to open it — you'll stay signed in, so you won't need another email link.
        </p>

        {/* ─── HERO: Desktop / Laptop (recommended, dominant) ─── */}
        <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/[0.04] p-6 text-left mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
              <Monitor className="w-5 h-5 text-emerald-400" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold text-[#F5F1EA]">Laptop or desktop</span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">Keep {businessName} one click away on this computer.</p>
            </div>
          </div>
          {installPrompt ? (
            <button
              onClick={onInstall}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0A0A] font-semibold text-base px-6 py-3.5 rounded-xl transition-colors shadow-sm"
            >
              Add {businessName}
            </button>
          ) : (
            <p className="text-sm text-slate-300 leading-relaxed bg-slate-900/60 border border-slate-800 rounded-lg px-4 py-3">
              Open this page in <span className="font-medium text-[#F5F1EA]">Chrome or Edge</span>, then choose <span className="font-medium text-[#F5F1EA]">Add to Home Screen</span> or <span className="font-medium text-[#F5F1EA]">Install App</span> from the browser menu.
            </p>
          )}
        </div>

        {/* ─── SECONDARY: phone options ─── */}
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500 text-left mb-3">
          Also add it on your phone
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
          {/* iPhone — always manual */}
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-left">
            <div className="flex items-center gap-2 mb-2">
              <Share className="w-4 h-4 text-slate-300 flex-shrink-0" strokeWidth={2} />
              <span className="text-sm font-semibold text-[#F5F1EA]">iPhone</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Tap <span className="text-slate-200 font-medium">Share</span>, then <span className="text-slate-200 font-medium">Add to Home Screen</span>.
              </p>
            </div>

          {/* Android — native install when available, else instruction */}
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-left">
            <div className="flex items-center gap-2 mb-2">
              <Smartphone className="w-4 h-4 text-slate-300 flex-shrink-0" strokeWidth={2} />
              <span className="text-sm font-semibold text-[#F5F1EA]">Android</span>
            </div>
            {installPrompt ? (
              <button
                onClick={onInstall}
                className="inline-flex items-center gap-1.5 bg-transparent border border-slate-700 hover:border-slate-600 hover:bg-slate-800 text-[#F5F1EA] font-medium text-xs px-3.5 py-2 rounded-lg transition-colors"
              >
                Add {businessName}
              </button>
            ) : (
              <p className="text-xs text-slate-400 leading-relaxed">
                Open in <span className="text-slate-200 font-medium">Chrome</span>, then choose <span className="text-slate-200 font-medium">Add to Home Screen</span> or <span className="text-slate-200 font-medium">Install App</span> from the browser menu.
              </p>
            )}
          </div>
        </div>

        {/* Quiet escape — install does not dismiss this screen, so users are
            never stranded. Deliberately subordinate to the install menu. */}
        <button
          onClick={onShowOS}
          className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
        >
          Open your workspace without adding it →
        </button>

      </div>
    </div>
  );
}
